import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import QRCode from 'qrcode';

const formatCurrency = (amountCents, currency = 'usd') => {
  const cents = Number(amountCents);
  const amount = Number.isFinite(cents) ? cents / 100 : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDollarCurrency = (value) => {
  const amount = Number(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
};

const formatLabel = (value, fallback = '') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

function StripeSaleSuccess() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const canceled = params.get('canceled') === 'true';
  const saleLabel = params.get('sale') || 'Sale';
  const sessionId = params.get('session_id') || '';
  const connectedAccountId = params.get('acct') || '';
  const [verificationStatus, setVerificationStatus] = useState('checking');
  const [ticketSummary, setTicketSummary] = useState(null);
  const [showCheckoutTicket, setShowCheckoutTicket] = useState(false);
  const [ticketQrDataUrl, setTicketQrDataUrl] = useState('');
  const paymentIsPaid = verificationStatus === 'paid';
  const isChecking = verificationStatus === 'checking';
  const showPendingTicket = (
    showCheckoutTicket || (!canceled && !isChecking)
  ) && !paymentIsPaid;
  const transaction = ticketSummary?.ticket?.transaction || null;
  const ticketItems = Array.isArray(ticketSummary?.ticket?.items) ? ticketSummary.ticket.items : [];
  const ticketAmount = transaction?.total_amount != null
    ? formatDollarCurrency(transaction.total_amount)
    : formatCurrency(ticketSummary?.amount_total, ticketSummary?.currency);
  const ticketTax = transaction?.tax_amount != null ? formatDollarCurrency(transaction.tax_amount) : null;
  const ticketStatus = formatLabel(transaction?.status || ticketSummary?.status, 'Pending Payment');
  const ticketPaymentMethod = formatLabel(transaction?.payment_method, 'Card');
  const ticketNumber = transaction?.sale_trans_number || ticketSummary?.sale_trans_number || '';
  const iconClassName = paymentIsPaid
    ? 'bg-green-100 text-green-600'
    : 'bg-amber-100 text-amber-600';
  const pageTitle = paymentIsPaid
    ? 'Payment Complete'
    : showCheckoutTicket
      ? 'Checkout Ticket'
      : canceled
        ? 'Payment Canceled'
        : 'Checkout Ticket';
  const pageMessage = paymentIsPaid
    ? 'Show this screen to the checkout person.'
    : showCheckoutTicket
      ? 'Show this checkout ticket to the checkout person.'
      : canceled
        ? 'Card payment was not completed. Return to the checkout ticket and show it to the checkout person.'
        : 'Show this checkout ticket to the checkout person for cash or another payment method.';

  useEffect(() => {
    if (!sessionId || !connectedAccountId) {
      setVerificationStatus('unavailable');
      return;
    }

    let cancelled = false;
    const confirmPayment = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe/confirm-sale-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            connectedAccountId,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data?.status === 'paid') {
          setVerificationStatus('paid');
          setTicketSummary(data);
        } else {
          setVerificationStatus('pending');
          setTicketSummary(data || null);
        }
      } catch {
        if (!cancelled) setVerificationStatus('pending');
      }
    };

    confirmPayment();
    return () => {
      cancelled = true;
    };
  }, [canceled, connectedAccountId, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const checkoutUrl = String(ticketSummary?.checkout_url || '').trim();
    if (!showPendingTicket || !checkoutUrl) {
      setTicketQrDataUrl('');
      return () => {
        cancelled = true;
      };
    }

    QRCode.toDataURL(checkoutUrl, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 260,
    })
      .then((dataUrl) => {
        if (!cancelled) setTicketQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setTicketQrDataUrl('');
      });

    return () => {
      cancelled = true;
    };
  }, [showPendingTicket, ticketSummary?.checkout_url]);

  if (showCheckoutTicket && isChecking) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <span className="text-3xl" aria-hidden="true">!</span>
          </div>
          <h1 className="mt-5 text-3xl font-bold text-slate-900">Checkout Ticket</h1>
          <p className="mt-3 text-lg text-slate-700">Loading checkout ticket...</p>
        </div>
      </div>
    );
  }

  if (showPendingTicket) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-10">
        <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-gray-500">Sale Recorded</p>
          <p className="mt-2 text-6xl font-bold tracking-tight text-gray-900">{ticketAmount}</p>
          <p className="mt-2 text-base text-gray-600">
            Amount to Collect{ticketTax ? ` (Includes ${ticketTax} Tax)` : ''}
          </p>
          <p className="mt-1 text-base font-medium text-gray-700">
            Payment: {ticketPaymentMethod}
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2 text-sm">
            <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700">
              {ticketNumber ? `Transaction #${ticketNumber}` : saleLabel}
            </span>
            <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-800">
              Status: {ticketStatus}
            </span>
          </div>
          <div className="mt-3 text-sm text-gray-600">
            <div>{transaction?.sale_name || saleLabel}</div>
          </div>

          <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4">
            <p className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-900">
              Customer Payment QR
            </p>
            <p className="mt-1 text-sm text-blue-900">
              Show this checkout ticket to the checkout person. If paying by card again, use the payment link.
            </p>
            <div className="mt-3 flex justify-center">
              {ticketQrDataUrl ? (
                <img
                  src={ticketQrDataUrl}
                  alt="Stripe payment QR code"
                  className="h-[220px] w-[220px] rounded-lg bg-white p-2 shadow-sm"
                />
              ) : (
                <div className="flex h-[220px] w-[220px] items-center justify-center rounded-lg bg-white text-sm text-gray-500 shadow-sm">
                  {ticketSummary?.checkout_url ? 'Creating QR...' : 'QR unavailable'}
                </div>
              )}
            </div>
            {ticketSummary?.checkout_url && (
              <a
                href={ticketSummary.checkout_url}
                className="mt-3 inline-block text-sm font-semibold text-blue-700 underline"
              >
                Open Card payment page
              </a>
            )}
            <p className="mt-4 border-t border-blue-100 pt-3 text-sm text-blue-900">
              If paying cash instead, return to the checkout person.
            </p>
          </div>

          {ticketItems.length > 0 && (
            <div className="mt-5 max-h-[240px] overflow-y-auto rounded-xl bg-[#F8F9FA] px-4 py-4 text-left">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                {ticketItems.reduce((sum, item) => sum + Math.max(1, Math.floor(Number(item?.quantity) || 1)), 0)} Items
              </p>
              <div className="space-y-2">
                {ticketItems.map((item, index) => {
                  const quantity = Math.max(1, Math.floor(Number(item?.quantity) || 1));
                  const lineAmount = Number(item?.line_subtotal);
                  return (
                    <div key={item?.id || `${item?.sales_trans_id}-${index}`} className="flex items-start justify-between gap-3 text-base">
                      <div className="min-w-0 text-gray-700">
                        <span className="mr-2 font-semibold text-gray-900">{quantity}x</span>
                        <span className="break-words">{item?.title || 'Untitled item'}</span>
                        {item?.category && (
                          <div className="ml-8 text-sm text-gray-500">{item.category}</div>
                        )}
                      </div>
                      <span className="shrink-0 font-semibold text-gray-900">
                        {formatDollarCurrency(Number.isFinite(lineAmount) ? lineAmount : 0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-center shadow-lg">
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${iconClassName}`}>
          {!paymentIsPaid ? (
            <span className="text-3xl" aria-hidden="true">!</span>
          ) : (
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        <h1 className="mt-5 text-3xl font-bold text-slate-900">
          {pageTitle}
        </h1>
        <p className="mt-3 text-lg text-slate-700">
          {pageMessage}
        </p>
        <div className="mt-5 rounded-lg bg-slate-50 px-4 py-4 text-slate-800">
          <p className="text-base font-semibold">{saleLabel}</p>
          {ticketSummary?.amount_total != null && (
            <p className="mt-2 text-5xl font-bold tracking-tight text-slate-900">
              {formatCurrency(ticketSummary.amount_total, ticketSummary.currency)}
            </p>
          )}
          {(canceled || showPendingTicket) && (
            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
              Card payment pending. Return to checkout for cash or another payment method.
            </p>
          )}
        </div>
        {canceled && !paymentIsPaid && (
          <button
            type="button"
            onClick={() => setShowCheckoutTicket(true)}
            className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            Return to Checkout Ticket
          </button>
        )}
        {canceled && verificationStatus === 'checking' && (
          <p className="mt-3 text-sm text-slate-500">
            Loading checkout ticket...
          </p>
        )}
        {canceled && verificationStatus !== 'checking' && !ticketSummary?.amount_total && (
          <p className="mt-3 text-sm text-slate-500">
            Please return to the checkout person to restart payment.
          </p>
        )}
        {!canceled && verificationStatus === 'checking' && (
          <p className="mt-3 text-sm text-slate-500">
            Verifying payment...
          </p>
        )}
        {showPendingTicket && verificationStatus !== 'checking' && !ticketSummary?.amount_total && (
          <p className="mt-3 text-sm text-slate-500">
            Please show this screen to the checkout person.
          </p>
        )}
      </div>
    </div>
  );
}

export default StripeSaleSuccess;


